const SCHEMA_VERSION_KEY = 'nettgrav:mls_schema_version';
const BUILD_VERSION_KEY = 'nettgrav:build_version';
const INDEXED_DB_NAMES = ['nettgrav-e2ee'];

export const MLS_SCHEMA_VERSION = 2;

const schemaVersionValue = String(MLS_SCHEMA_VERSION);

let ensurePromise = null;

const hasWindow = () => typeof window !== 'undefined';

const getLocalStorage = () => {
  if (!hasWindow()) {
    return null;
  }
  try {
    return window.localStorage || null;
  } catch {
    return null;
  }
};

const deleteDatabase = (name) => {
  if (typeof indexedDB === 'undefined' || !name) {
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    let settled = false;

    try {
      const request = indexedDB.deleteDatabase(name);
      request.onsuccess = () => {
        settled = true;
        resolve(true);
      };
      request.onerror = () => {
        settled = true;
        resolve(false);
      };
      request.onblocked = () => {
        if (!settled && typeof console?.warn === 'function') {
          try {
            console.warn('[MLS] IndexedDB deletion blocked', { name });
          } catch {}
        }
      };

      setTimeout(() => {
        if (!settled) {
          settled = true;
          resolve(false);
        }
      }, 1500);
    } catch {
      resolve(false);
    }
  });
};

const wipeIndexedDatabases = () => {
  if (!INDEXED_DB_NAMES.length) {
    return Promise.resolve();
  }
  return Promise.all(INDEXED_DB_NAMES.map((name) => deleteDatabase(name))).then(() => undefined);
};

const wipeLocalStorage = () => {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(SCHEMA_VERSION_KEY);
  } catch {}

  try {
    storage.removeItem(BUILD_VERSION_KEY);
  } catch {}
};

const persistSchemaVersion = () => {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }
  try {
    storage.setItem(SCHEMA_VERSION_KEY, schemaVersionValue);
  } catch (error) {
    if (typeof console?.warn === 'function') {
      try {
        console.warn('[MLS] Failed storing schema version', error);
      } catch {}
    }
  }
};

const readStoredVersion = () => {
  const storage = getLocalStorage();
  if (!storage) {
    return null;
  }
  try {
    const value = storage.getItem(SCHEMA_VERSION_KEY);
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  } catch {
    return null;
  }
};

// Be conservative: avoid wiping IndexedDB to preserve MLS identities/groups.
// We still clear our own schema/build markers so we can migrate forward.
const wipeStorage = async () => {
  wipeLocalStorage();
  // Intentionally do NOT wipe IndexedDB here to prevent identity churn.
  // If a hard reset is ever required, a dedicated maintenance task should do it.
};

export const ensureMlsStorageSchema = () => {
  if (!ensurePromise) {
    ensurePromise = (async () => {
      const storedVersion = readStoredVersion();
      if (storedVersion === schemaVersionValue) {
        return { reset: false };
      }

      if (typeof console?.info === 'function') {
        try {
          console.info('[MLS] Resetting local MLS storage due to schema mismatch', {
            storedVersion,
            targetVersion: schemaVersionValue,
          });
        } catch {}
      }

      await wipeStorage();
      persistSchemaVersion();
      return { reset: true };
    })().catch((error) => {
      ensurePromise = null;
      if (typeof console?.warn === 'function') {
        try {
          console.warn('[MLS] Failed ensuring MLS storage schema', error);
        } catch {}
      }
      throw error;
    });
  }

  return ensurePromise;
};
